import React, { useState, useEffect } from 'react';
import { HiOutlineOfficeBuilding, HiPlus, HiSearch, HiPencil, HiTrash, HiEye, HiOutlineUsers, HiOutlinePlus, HiOutlineX } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { companiesService } from '../services/companiesService';
import { contactsService } from '../services/contactsService';
import LoadingSpinner from '../components/LoadingSpinner';
import { Company, Contact, CreateCompanyData, CreateContactData } from '../types';
import { useEscapeModal, hasFormChanges } from '../utils/useEscapeModal';

interface CompanyFormData {
  name: string;
  location: string;
  website: string;
  description: string;
  starred: boolean;
  tier: 'high' | 'medium' | 'low';
}

interface NewContactData {
  name: string;
  title: string;
  email: string;
  phone: string;
  linkedin: string;
  relationship: string;
}

interface TempContact extends NewContactData {
  id: number;
}

interface RelationshipOption {
  value: string;
  label: string;
}

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('priority');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    location: '',
    website: '',
    description: '',
    starred: false,
    tier: 'medium'
  });
  const [contacts, setContacts] = useState<TempContact[]>([]);
  const [existingContacts, setExistingContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const [newContact, setNewContact] = useState<NewContactData>({
    name: '',
    title: '',
    email: '',
    phone: '',
    linkedin: '',
    relationship: 'other'
  });

  // Initial form data for change detection
  const [initialFormData, setInitialFormData] = useState<CompanyFormData>({
    name: '',
    location: '',
    website: '',
    description: '',
    starred: false,
    tier: 'medium'
  });
  const [initialContacts, setInitialContacts] = useState<TempContact[]>([]);

  const relationshipOptions: RelationshipOption[] = [
    { value: 'colleague', label: 'Colleague' },
    { value: 'manager', label: 'Manager' },
    { value: 'hr', label: 'HR' },
    { value: 'recruiter', label: 'Recruiter' },
    { value: 'friend', label: 'Friend' },
    { value: 'referral', label: 'Referral' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCompanies();
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Auto-refresh when window gains focus to ensure fresh data
  useEffect(() => {
    const handleFocus = () => {
      fetchCompanies();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanies = async (): Promise<void> => {
    try {
      setLoading(true);
      const params: any = { 
        sort: sortBy,
        limit: 100
      };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
              }
        const response = await companiesService.getCompanies(params);
        setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let company: Company;
      if (editingCompany) {
        company = await companiesService.updateCompany(editingCompany._id, formData);
        toast.success('Company updated successfully');
      } else {
        company = await companiesService.createCompany(formData as CreateCompanyData);
        toast.success('Company created successfully');
      }
      
      // Create contacts for the company if any were added
      if (contacts.length > 0) {
        for (const contact of contacts) {
          try {
            await contactsService.createContact({
              name: contact.name,
              title: contact.title || undefined,
              email: contact.email || undefined,
              phone: contact.phone || undefined,
              linkedin: contact.linkedin || undefined,
              relationship: contact.relationship as 'colleague' | 'manager' | 'hr' | 'recruiter' | 'friend' | 'referral' | 'other',
              howMet: 'other', // Default value for required field
              company: company._id || editingCompany?._id,
              priority: 'medium', // Default priority
              notes: '', // Default empty notes
              tags: [] // Default empty tags
            } as CreateContactData);
          } catch (error) {
            console.error('Error creating contact:', contact.name, error);
            toast.error(`Failed to create contact: ${contact.name}`);
          }
        }
        if (contacts.length > 0) {
          toast.success(`${contacts.length} contact${contacts.length > 1 ? 's' : ''} added successfully`);
        }
      }
      
      // Reset form first, then refresh data
      resetForm();
      await fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error(editingCompany ? 'Failed to update company' : 'Failed to create company');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (company: Company): Promise<void> => {
    if (window.confirm(`Are you sure you want to delete ${company.name}?`)) {
      try {
        // Optimistically remove from local state for immediate feedback
        setCompanies(companies.filter(c => c._id !== company._id));
        
        await companiesService.deleteCompany(company._id);
        toast.success('Company deleted successfully');
        
        // Refresh to ensure consistency
        await fetchCompanies();
      } catch (error) {
        console.error('Error deleting company:', error);
        toast.error('Failed to delete company');
        // Refresh on error to restore correct state
        await fetchCompanies();
      }
    }
  };



  const fetchExistingContacts = async (companyId: string): Promise<void> => {
    try {
      setLoadingContacts(true);
      const response = await contactsService.getContactsByCompany(companyId, { limit: 100 });
      setExistingContacts(response.data || []);
    } catch (error) {
      console.error('Error fetching existing contacts:', error);
      toast.error('Failed to load existing contacts');
      setExistingContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleEdit = (company: Company): void => {
    setEditingCompany(company);
    const editFormData = {
      name: company.name || '',
      location: company.location || '',
      website: company.website || '',
      description: company.description || '',
      starred: company.starred || false,
      tier: company.tier || 'medium' as const
    };
    setFormData(editFormData);
    setInitialFormData(editFormData);
    // Reset new contacts and fetch existing ones
    setContacts([]);
    setInitialContacts([]);
    fetchExistingContacts(company._id);
    setShowCreateModal(true);
  };

  const addContact = (): void => {
    if (newContact.name.trim()) {
      setContacts([...contacts, { ...newContact, id: Date.now() }]);
      setNewContact({
        name: '',
        title: '',
        email: '',
        phone: '',
        linkedin: '',
        relationship: 'other'
      });
    }
  };

  const removeContact = (contactId: number): void => {
    setContacts(contacts.filter(contact => contact.id !== contactId));
  };

  const handleEditExistingContact = (contact: Contact): void => {
    // Navigate to contact detail page for editing
    navigate(`/contacts/${contact._id}`);
  };

  const handleDeleteExistingContact = async (contactId: string): Promise<void> => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await contactsService.deleteContact(contactId);
        toast.success('Contact deleted successfully');
        // Refresh existing contacts
        if (editingCompany) {
          await fetchExistingContacts(editingCompany._id);
        }
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
      }
    }
  };

  const resetForm = (): void => {
    const defaultFormData = {
      name: '',
      location: '',
      website: '',
      description: '',
      starred: false,
      tier: 'medium' as const
    };
    
    setFormData(defaultFormData);
    setInitialFormData(defaultFormData);
    setContacts([]);
    setInitialContacts([]);
    setExistingContacts([]);
    setNewContact({
      name: '',
      title: '',
      email: '',
      phone: '',
      linkedin: '',
      relationship: 'other'
    });
    setEditingCompany(null);
    setIsSubmitting(false);
    setShowCreateModal(false);
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = hasFormChanges(formData, initialFormData) || 
                           JSON.stringify(contacts) !== JSON.stringify(initialContacts);

  // Handle ESC key to close modal with confirmation for unsaved changes
  useEscapeModal({
    isOpen: showCreateModal,
    onClose: resetForm,
    hasUnsavedChanges,
    confirmMessage: 'You have unsaved changes. Are you sure you want to close without saving?'
  });

  // Companies are filtered on the server side now
  const filteredCompanies = companies;

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Companies</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Manage and track the companies you're interested in.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="btn btn-primary btn-md flex items-center gap-2"
        >
          <HiPlus className="h-4 w-4" />
          <span>Add Company</span>
        </button>
      </div>

      {/* Search and Sort */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="w-48">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input w-full"
          >
            <option value="priority">Priority</option>
            <option value="rating">Rating</option>
            <option value="lastInteraction">Last Interaction</option>
            <option value="name">Name</option>
            <option value="created">Date Created</option>
          </select>
        </div>
      </div>

      {/* Companies List */}
      <div className="card">
        <div className="card-content">
          {filteredCompanies.length === 0 ? (
            <div className="text-center py-12">
              <HiOutlineOfficeBuilding className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900">
                {companies.length === 0 ? 'No companies yet' : 'No companies found'}
              </h3>
              <p className="mt-1 text-sm text-secondary-500">
                {companies.length === 0 
                  ? 'Get started by adding your first company.'
                  : 'Try adjusting your search criteria.'
                }
              </p>
              {companies.length === 0 && (
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="mt-4 btn btn-primary btn-md"
                >
                  Add your first company
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Website
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {filteredCompanies.map((company) => (
                    <tr key={company._id} className={`hover:bg-secondary-50 ${company.starred ? 'bg-secondary-200' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                              <HiOutlineOfficeBuilding className="h-5 w-5 text-primary-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div 
                              className="text-sm font-medium text-secondary-900 cursor-pointer hover:text-primary-600"
                              onClick={() => navigate(`/companies/${company._id}`)}
                            >
                              {company.name}
                            </div>
                            {company.description && (
                              <div className="text-sm text-secondary-500 truncate max-w-xs">
                                {company.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {company.location || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          company.tier === 'high' 
                            ? 'bg-red-100 text-red-800' 
                            : company.tier === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {company.tier?.charAt(0).toUpperCase() + company.tier?.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {company.website ? (
                          <a 
                            href={company.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:text-primary-600 underline"
                          >
                            Visit site
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/companies/${company._id}`)}
                            className="btn btn-ghost btn-sm p-2"
                            title="View"
                          >
                            <HiEye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(company)}
                            className="btn btn-ghost btn-sm p-2"
                            title="Edit"
                          >
                            <HiPencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(company)}
                            className="btn btn-ghost btn-sm p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <HiTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">
                {editingCompany ? 'Edit Company' : 'Add New Company'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Company Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-secondary-900 flex items-center gap-2">
                    <HiOutlineOfficeBuilding className="h-5 w-5" />
                    Company Information
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="input w-full"
                        placeholder="Enter company name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Location
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="input w-full"
                        placeholder="e.g., San Francisco, CA"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="input w-full"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="input w-full min-h-[80px] resize-none"
                      rows={3}
                      placeholder="Notes about the company..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Priority Tier
                      </label>
                      <select
                        value={formData.tier}
                        onChange={(e) => setFormData({ ...formData, tier: e.target.value as 'high' | 'medium' | 'low' })}
                        className="input w-full"
                      >
                        <option value="high">High Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="low">Low Priority</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="starred"
                        checked={formData.starred}
                        onChange={(e) => setFormData({ ...formData, starred: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                      />
                      <label htmlFor="starred" className="ml-2 block text-sm text-secondary-900">
                        Star this company (keeps it at the top)
                      </label>
                    </div>
                  </div>
                </div>

                {/* Contacts Section */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-secondary-900 flex items-center gap-2">
                    <HiOutlineUsers className="h-5 w-5" />
                    Contacts
                    <span className="text-sm font-normal text-secondary-500">
                      (Optional - Add contacts for this company)
                    </span>
                  </h4>

                  {/* Existing Contacts (when editing) */}
                  {editingCompany && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h5 className="text-sm font-medium text-secondary-700">
                          Current Contacts
                        </h5>
                        {loadingContacts && (
                          <div className="text-sm text-secondary-500">Loading...</div>
                        )}
                      </div>
                      
                      {existingContacts.length > 0 ? (
                        <div className="space-y-2">
                          {existingContacts.map((contact) => (
                            <div key={contact._id} className="flex items-center justify-between p-3 border border-secondary-200 rounded-lg bg-white">
                              <div className="flex-1">
                                <div className="font-medium text-secondary-900">{contact.name}</div>
                                <div className="text-sm text-secondary-600">
                                  {contact.title && `${contact.title} • `}
                                  {relationshipOptions.find(r => r.value === contact.relationship)?.label || contact.relationship}
                                </div>
                                {(contact.email || contact.phone || contact.linkedin) && (
                                  <div className="text-sm text-secondary-500">
                                    {contact.email && contact.email}
                                    {contact.email && (contact.phone || contact.linkedin) && ' • '}
                                    {contact.linkedin && (
                                      <a 
                                        href={contact.linkedin} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:text-primary-700"
                                      >
                                        LinkedIn
                                      </a>
                                    )}
                                    {contact.linkedin && contact.phone && ' • '}
                                    {contact.phone && contact.phone}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 ml-3">
                                <button
                                  type="button"
                                  onClick={() => handleEditExistingContact(contact)}
                                  className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                  title="Edit contact"
                                >
                                  <HiPencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteExistingContact(contact._id)}
                                  className="p-2 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete contact"
                                >
                                  <HiTrash className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : !loadingContacts ? (
                        <div className="text-center py-4 text-sm text-secondary-500 bg-secondary-50 rounded-lg">
                          No existing contacts for this company
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Add New Contact Form */}
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-secondary-700">
                      Add New Contact
                    </h5>
                    <div className="bg-secondary-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Contact Name
                        </label>
                        <input
                          type="text"
                          value={newContact.name}
                          onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                          className="input w-full"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={newContact.title}
                          onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                          className="input w-full"
                          placeholder="Hiring Manager"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={newContact.email}
                          onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                          className="input w-full"
                          placeholder="john@company.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          LinkedIn Profile
                        </label>
                        <input
                          type="url"
                          value={newContact.linkedin}
                          onChange={(e) => setNewContact({ ...newContact, linkedin: e.target.value })}
                          className="input w-full"
                          placeholder="https://linkedin.com/in/username"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={newContact.phone}
                          onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                          className="input w-full"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Relationship
                        </label>
                        <select
                          value={newContact.relationship}
                          onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
                          className="input w-full"
                        >
                          {relationshipOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={addContact}
                          disabled={!newContact.name.trim()}
                          className="btn btn-primary btn-sm w-full"
                        >
                          <HiOutlinePlus className="h-4 w-4 mr-1" />
                          Add Contact
                        </button>
                      </div>
                    </div>
                  </div>
                  </div>

                  {/* Added Contacts List */}
                  {contacts.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-secondary-700">
                        Added Contacts ({contacts.length})
                      </h5>
                      <div className="space-y-2">
                        {contacts.map((contact) => (
                          <div key={contact.id} className="flex items-center justify-between p-3 border border-secondary-200 rounded-lg bg-white">
                            <div>
                              <div className="font-medium text-secondary-900">{contact.name}</div>
                              <div className="text-sm text-secondary-600">
                                {contact.title && `${contact.title} • `}
                                {relationshipOptions.find(r => r.value === contact.relationship)?.label}
                              </div>
                              {(contact.email || contact.phone || contact.linkedin) && (
                                <div className="text-sm text-secondary-500">
                                  {contact.email && contact.email}
                                  {contact.email && (contact.phone || contact.linkedin) && ' • '}
                                  {contact.linkedin && (
                                    <a 
                                      href={contact.linkedin} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-primary-600 hover:text-primary-700"
                                    >
                                      LinkedIn
                                    </a>
                                  )}
                                  {contact.linkedin && contact.phone && ' • '}
                                  {contact.phone && contact.phone}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeContact(contact.id)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <HiOutlineX className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="btn btn-secondary btn-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-primary btn-md"
                  >
                    {isSubmitting 
                      ? (editingCompany ? 'Updating...' : 'Creating...')
                      : (editingCompany ? 'Update Company' : 'Create Company')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies; 